import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import { listMyCompetitions } from "../../../lib/clientCompetitions";
import type { ClientCompetitionListRow } from "../../../types/database";
import { getMyCompanies } from "../../../lib/companies";
import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import {
  inviteManager,
  listAssignmentsForManager,
  listManagersForCompany,
  removeManager,
  setManagerAssignments,
  type ManagerRow,
} from "../../../lib/managers";

const THEME = PANE_THEMES.Client;

const FOREST = THEME.sidebarBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const FOREST_DEEP = COLORS.FOREST_DEEP.hex;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const RED = THEME.destructive;

export default function ClientTeamScreen() {
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [managers, setManagers] = useState<ManagerRow[]>([]);
  const [competitions, setCompetitions] = useState<ClientCompetitionListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ManagerRow | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErrMsg(null);
    try {
      const [cs, comps] = await Promise.all([getMyCompanies(), listMyCompetitions()]);
      const cid = cs[0]?.id ?? null;
      setCompanyId(cid);
      setCompetitions(comps);
      if (cid) setManagers(await listManagersForCompany(cid));
      else setManagers([]);
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : "Failed to load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void reload();
    }, [reload])
  );

  if (loading) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center", backgroundColor: THEME.pageBg }}>
        <ActivityIndicator color={AMBER} />
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 64 }}>
      <View style={{ paddingHorizontal: 32, paddingTop: 28, maxWidth: 1080 }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            People · Team
          </Text>
        </View>
        <View style={{ marginTop: 6, flexDirection: "row", alignItems: "baseline", gap: 16, flexWrap: "wrap" }}>
          <Text
            style={{
              flex: 1,
              fontFamily: FONTS.display,
              fontSize: 36,
              lineHeight: 40,
              color: W,
            }}
          >
            Your team
          </Text>
          {companyId ? (
            <Pressable
              onPress={() => setInviteOpen(true)}
              style={[
                {
                  paddingHorizontal: 18,
                  paddingVertical: 10,
                  borderRadius: 3,
                  backgroundColor: AMBER,
                } as ViewStyle,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 11,
                  letterSpacing: 1.6,
                  textTransform: "uppercase",
                  color: FOREST,
                }}
              >
                + Invite manager
              </Text>
            </Pressable>
          ) : null}
        </View>
        <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 13, color: WM, maxWidth: 720 }}>
          Invite team members to manage your competitions on Coravu. Each manager sees only the
          competitions you assign them to — no access to strategic decisions like prize structure
          or rubric weights.
        </Text>

        {errMsg ? (
          <Text style={{ marginTop: 14, fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: RED }}>{errMsg}</Text>
        ) : null}
        {statusMsg ? (
          <Text style={{ marginTop: 14, fontFamily: FONTS.bodyMedium, fontSize: 12, color: SAGE }}>{statusMsg}</Text>
        ) : null}

        {!companyId ? (
          <Text style={{ marginTop: 22, fontFamily: FONTS.body, fontSize: 13, color: WL }}>
            No company on file for your account. Reach out to luke@coravu.com.
          </Text>
        ) : managers.length === 0 ? (
          <View
            style={{
              marginTop: 22,
              padding: 22,
              borderRadius: 8,
              borderWidth: 1,
              borderStyle: "dashed",
              borderColor: BORDER,
            }}
          >
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: LINEN }}>
              No team members yet
            </Text>
            <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
              Invite a manager to delegate per-competition operational work.
            </Text>
          </View>
        ) : (
          <View style={{ marginTop: 22, gap: 10 }}>
            {managers.map((m) => (
              <ManagerCard
                key={m.user_id}
                manager={m}
                onEdit={() => setEditTarget(m)}
                onRemoved={() => {
                  setStatusMsg(`Removed ${m.full_name ?? m.email} from the team.`);
                  setTimeout(() => setStatusMsg(null), 3000);
                  void reload();
                }}
                onError={(msg) => setErrMsg(msg)}
                companyId={companyId}
              />
            ))}
          </View>
        )}
      </View>

      {inviteOpen && companyId ? (
        <InviteModal
          companyId={companyId}
          onClose={() => setInviteOpen(false)}
          onSent={(email) => {
            setInviteOpen(false);
            setStatusMsg(`Invite created for ${email}.`);
            setTimeout(() => setStatusMsg(null), 4000);
            void reload();
          }}
        />
      ) : null}

      {editTarget && companyId ? (
        <EditAssignmentsModal
          manager={editTarget}
          companyId={companyId}
          competitions={competitions}
          onClose={() => setEditTarget(null)}
          onSaved={(count) => {
            setEditTarget(null);
            setStatusMsg(`Saved — ${editTarget.full_name ?? editTarget.email} now manages ${count} competition${count === 1 ? "" : "s"}.`);
            setTimeout(() => setStatusMsg(null), 3500);
            void reload();
          }}
        />
      ) : null}
    </ScrollView>
  );
}

function ManagerCard({
  manager,
  companyId,
  onEdit,
  onRemoved,
  onError,
}: {
  manager: ManagerRow;
  companyId: string;
  onEdit: () => void;
  onRemoved: () => void;
  onError: (msg: string) => void;
}) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);

  async function remove() {
    setBusy(true);
    const { error } = await removeManager(manager.user_id, companyId);
    setBusy(false);
    if (error) {
      onError(error.message);
      return;
    }
    onRemoved();
  }

  const initials = (manager.full_name ?? manager.email).split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <View
      style={{
        padding: 16,
        borderRadius: 6,
        backgroundColor: FOREST_DEEP,
        borderWidth: 1,
        borderColor: BORDER,
        flexDirection: "row",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
      }}
    >
      <View
        style={{
          width: 40,
          height: 40,
          borderRadius: 999,
          backgroundColor: "rgba(181,135,58,0.16)",
          alignItems: "center",
          justifyContent: "center",
          borderWidth: 1,
          borderColor: "rgba(181,135,58,0.32)",
        }}
      >
        <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 16, color: AMBER }}>{initials}</Text>
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 14, color: LINEN }} numberOfLines={1}>
          {manager.full_name ?? manager.email}
        </Text>
        <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11.5, color: WM }} numberOfLines={1}>
          {manager.email}
        </Text>
      </View>
      <View style={{ minWidth: 140 }}>
        <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: AMBER }}>
          {manager.assigned_count}
        </Text>
        <Text style={{ fontFamily: FONTS.body, fontSize: 10.5, color: WL }}>
          competition{manager.assigned_count === 1 ? "" : "s"} assigned
        </Text>
      </View>
      <Pressable
        onPress={onEdit}
        style={[
          {
            paddingHorizontal: 12,
            paddingVertical: 7,
            borderRadius: 3,
            borderWidth: 1,
            borderColor: BORDER,
          } as ViewStyle,
          Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
        ]}
      >
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10.5, letterSpacing: 1.4, color: AMBER }}>
          Edit assignments
        </Text>
      </Pressable>
      <Pressable
        onPress={() => {
          if (busy) return;
          if (armed) {
            setArmed(false);
            void remove();
          } else {
            setArmed(true);
            setTimeout(() => setArmed(false), 4000);
          }
        }}
        style={[
          { paddingHorizontal: 12, paddingVertical: 7 } as ViewStyle,
          Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
        ]}
      >
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10.5, letterSpacing: 1.4, color: armed ? RED : WS }}>
          {armed ? "Click again to remove" : "Remove"}
        </Text>
      </Pressable>
    </View>
  );
}

function InviteModal({
  companyId,
  onClose,
  onSent,
}: {
  companyId: string;
  onClose: () => void;
  onSent: (email: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function send() {
    setErr(null);
    if (!/^.+@.+\..+$/.test(email.trim())) {
      setErr("Enter a valid email address.");
      return;
    }
    setBusy(true);
    const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(" ") || null;
    const { error } = await inviteManager({ email, companyId, fullName });
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onSent(email.trim());
  }

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.65)",
          justifyContent: "center",
          paddingHorizontal: 16,
        }}
      >
        <View
          style={{
            maxWidth: 480,
            width: "100%",
            alignSelf: "center",
            backgroundColor: FOREST_MID,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: BORDER,
            padding: 22,
          }}
        >
          <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
            Invite a manager
          </Text>
          <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12.5, color: WM }}>
            They'll get an email with a magic link. After they accept, you'll assign them to
            specific competitions.
          </Text>

          {err ? (
            <Text style={{ marginTop: 10, fontFamily: FONTS.bodyMedium, fontSize: 12, color: RED }}>{err}</Text>
          ) : null}

          <FieldLabel required>Email</FieldLabel>
          <DarkInput value={email} onChange={setEmail} placeholder="alex@example.com" keyboardType="email-address" />

          <View style={{ flexDirection: "row", gap: 10 }}>
            <View style={{ flex: 1 }}>
              <FieldLabel>First name</FieldLabel>
              <DarkInput value={firstName} onChange={setFirstName} placeholder="Alex" autoCapitalize="words" />
            </View>
            <View style={{ flex: 1 }}>
              <FieldLabel>Last name</FieldLabel>
              <DarkInput value={lastName} onChange={setLastName} placeholder="Patel" autoCapitalize="words" />
            </View>
          </View>

          <View style={{ marginTop: 18, flexDirection: "row", justifyContent: "flex-end", gap: 14 }}>
            <Pressable
              onPress={onClose}
              style={[
                { paddingHorizontal: 14, paddingVertical: 10 },
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WM }}>Cancel</Text>
            </Pressable>
            <Pressable
              onPress={() => void send()}
              disabled={busy}
              style={[
                {
                  paddingHorizontal: 18,
                  paddingVertical: 10,
                  borderRadius: 3,
                  backgroundColor: AMBER,
                  opacity: busy ? 0.5 : 1,
                } as ViewStyle,
                Platform.OS === "web" && !busy ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 11,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: FOREST,
                }}
              >
                {busy ? "Sending…" : "Send invite"}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function EditAssignmentsModal({
  manager,
  companyId,
  competitions,
  onClose,
  onSaved,
}: {
  manager: ManagerRow;
  companyId: string;
  competitions: ClientCompetitionListRow[];
  onClose: () => void;
  onSaved: (count: number) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const ids = await listAssignmentsForManager(manager.user_id);
      if (alive) {
        setSelected(new Set(ids));
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [manager.user_id]);

  // Only assign to ACTIVE competitions (open / judging / closed) —
  // draft comps haven't been published, so assignment is meaningless
  // until they go open.
  const assignable = useMemo(
    () => competitions.filter((c) => c.status !== "draft"),
    [competitions]
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setErr(null);
    const ids = Array.from(selected);
    const { error } = await setManagerAssignments(manager.user_id, companyId, ids, null);
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onSaved(ids.length);
  }

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.65)",
          justifyContent: "center",
          paddingHorizontal: 16,
        }}
      >
        <View
          style={{
            maxWidth: 560,
            width: "100%",
            maxHeight: "82%",
            alignSelf: "center",
            backgroundColor: FOREST_MID,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: BORDER,
          }}
        >
          <View style={{ paddingHorizontal: 22, paddingTop: 22 }}>
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              Assign competitions
            </Text>
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12.5, color: WM }}>
              {manager.full_name ?? manager.email} can manage the competitions you check below.
            </Text>
          </View>

          <ScrollView style={{ paddingHorizontal: 22, marginTop: 14 }}>
            {loading ? (
              <ActivityIndicator color={AMBER} />
            ) : assignable.length === 0 ? (
              <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: WL, paddingVertical: 12 }}>
                No published competitions yet. Publish a draft first, then come back to assign.
              </Text>
            ) : (
              <View style={{ gap: 6, paddingBottom: 14 }}>
                {assignable.map((c) => {
                  const checked = selected.has(c.id);
                  return (
                    <Pressable
                      key={c.id}
                      onPress={() => toggle(c.id)}
                      style={[
                        {
                          paddingHorizontal: 14,
                          paddingVertical: 12,
                          borderRadius: 4,
                          borderWidth: 1,
                          borderColor: checked ? AMBER : BORDER,
                          backgroundColor: checked ? "rgba(181,135,58,0.10)" : "rgba(255,255,255,0.02)",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 12,
                        } as ViewStyle,
                        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                      ]}
                    >
                      <View
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 3,
                          borderWidth: 1.5,
                          borderColor: checked ? AMBER : BORDER,
                          backgroundColor: checked ? AMBER : "transparent",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {checked ? (
                          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, color: FOREST }}>✓</Text>
                        ) : null}
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: W }} numberOfLines={1}>
                          {c.name}
                        </Text>
                        <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11, color: WM }}>
                          status {c.status}
                        </Text>
                      </View>
                    </Pressable>
                  );
                })}
              </View>
            )}
          </ScrollView>

          <View
            style={{
              paddingHorizontal: 22,
              paddingVertical: 16,
              borderTopWidth: 1,
              borderTopColor: BORDER,
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            {err ? (
              <Text style={{ flex: 1, fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: RED }}>{err}</Text>
            ) : null}
            <Pressable
              onPress={onClose}
              style={[
                { paddingHorizontal: 14, paddingVertical: 10 },
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WM }}>Cancel</Text>
            </Pressable>
            <Pressable
              onPress={() => void save()}
              disabled={busy}
              style={[
                {
                  paddingHorizontal: 18,
                  paddingVertical: 10,
                  borderRadius: 3,
                  backgroundColor: AMBER,
                  opacity: busy ? 0.5 : 1,
                } as ViewStyle,
                Platform.OS === "web" && !busy ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 11,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: FOREST,
                }}
              >
                {busy ? "Saving…" : "Save assignments"}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function FieldLabel({ children, required }: { children: string; required?: boolean }) {
  return (
    <View style={{ marginTop: 14, flexDirection: "row", alignItems: "center", gap: 4 }}>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: WM,
        }}
      >
        {children}
      </Text>
      {required ? <Text style={{ color: RED, fontSize: 10 }}>*</Text> : null}
    </View>
  );
}

function DarkInput({
  value,
  onChange,
  placeholder,
  keyboardType,
  autoCapitalize,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  keyboardType?: "default" | "email-address" | "number-pad";
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
}) {
  return (
    <TextInput
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      placeholderTextColor={WL}
      keyboardType={keyboardType}
      autoCapitalize={autoCapitalize}
      style={
        {
          marginTop: 6,
          backgroundColor: FOREST_DEEP,
          borderWidth: 1,
          borderColor: BORDER,
          borderRadius: 4,
          paddingHorizontal: 12,
          paddingVertical: 10,
          color: W,
          fontFamily: FONTS.body,
          fontSize: 13,
          ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
        } as unknown as TextStyle
      }
    />
  );
}
